import { createColumnHelper } from "@tanstack/react-table";
import { EstimateStatusBadge } from "@/components/dashboard/EstimateStatusBadge";
import User from "@/components/dashboard/User";
import { FilesData } from "@/components/dashboard/FilesData/FilesData";
import type { Estimation } from "@/interfaces/estimation.interface";
import { formatDate } from "@/util/transform";
import { capitalizeWords, EstimateStatus, getCrmCreationSourceDisplayName } from "@rebuild/shared";

import { ESTIMATE_TYPE_LABELS } from "@/enums/estimate-type";
import { getDocumentationCount } from "@/pages/dashboard/utils/file";
import { MapPin } from "lucide-react";
import { User as UserIcon, Calendar } from "lucide-react";


const columnHelper = createColumnHelper<Estimation>();

export const columns = [
  columnHelper.accessor("projectName", {
    header: "Project name",
    cell: (info) => <p className="truncate font-medium">{capitalizeWords(info.getValue())}</p>,
  }),
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => <EstimateStatusBadge status={info.getValue()?.length ? info.getValue() : EstimateStatus.RECEIVED} />,
    enableSorting: true,
  }),
  columnHelper.accessor("estimateType", {
    header: "Type",
    cell: (info) => <p className="font-medium text-muted-foreground">{info.getValue() ? ESTIMATE_TYPE_LABELS[info.getValue()!] : "-"}</p>,
  }),
  columnHelper.accessor("estimateReferenceNumber", {
    header: "Reference",
    cell: (info) => <p className="font-medium text-[#71717A]">{info.getValue() || ""}</p>,
  }),
  columnHelper.accessor("propertyAddress", {
    header: () => (
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4" />
        Address
      </div>
    ),
    cell: (info) => info.getValue() ?? "N/A",
  }),
  columnHelper.accessor("assignee", {
    header: () => (
      <div className="flex items-center gap-2">
        <UserIcon className="h-4 w-4" />
        Assignee
      </div>
    ),
    cell: (info) => {
      const assigneeId = info.getValue();
      const user = assigneeId ? info.table.options.meta?.findUserById?.(assigneeId) : undefined;
      return <User photo={user?.photoURL ?? ""} name={user ? `${user.firstName} ${user.lastName}` : undefined} />;
    },
    sortingFn: (rowA, rowB, columnId) => {
      const a = rowA.getValue<string>(columnId);
      const b = rowB.getValue<string>(columnId);
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b);
    },
  }),
  columnHelper.accessor("createdBy", {
    header: () => (
      <div className="flex items-center gap-2">
        <UserIcon className="h-4 w-4" />
        Created by
      </div>
    ),
    cell: (info) => {
      const createdBy = info.getValue();
      const name = [createdBy?.firstName, createdBy?.lastName].filter(Boolean).join(" ");
      if (name) {
        return <User photo={createdBy?.photoURL ?? ""} name={name} />;
      }

      const crmSourceDisplayName = getCrmCreationSourceDisplayName(info.row.original.creationSource);
      if (crmSourceDisplayName) {
        return <p>{crmSourceDisplayName}</p>;
      }

      return <p className="text-muted-foreground">n/a</p>;
    },

    // The API paginates by creation time. Sorting one page of creator names would
    // not match the displayed names or produce a meaningful organization-wide order.
    enableSorting: false,
  }),
  columnHelper.accessor("dateCreated", {
    header: () => (
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4" />
        Created
      </div>
    ),
    cell: (info) => <p className="font-medium text-[#71717A]">{formatDate(info.getValue())}</p>,
    sortingFn: (rowA, rowB) => {
      const dateA = new Date(rowA.original.dateCreated);
      const dateB = new Date(rowB.original.dateCreated);
      const timeA = dateA.getTime();
      const timeB = dateB.getTime();

      if (isNaN(timeA) && isNaN(timeB)) return 0;
      if (isNaN(timeA)) return 1;
      if (isNaN(timeB)) return -1;

      if (timeA === timeB) {
        const tsA = rowA.original.timestampCreated || 0;
        const tsB = rowB.original.timestampCreated || 0;
        return tsA - tsB;
      }

      return timeA - timeB;
    },
  }),
  columnHelper.accessor("fieldData", {
    header: "Field Data",
    enableSorting: false,
    meta: { bodyClassName: "!w-[130px] !min-w-[130px] !max-w-[130px]" },
    cell: (info) => {
      const filesCount = getDocumentationCount(info.row.original);
      return <FilesData filesCount={filesCount} />;
    },
  }),
];
